// src/pages/reports/ReportsTrFilterPopup.tsx
import React, {
  Dispatch,
  SetStateAction,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
  GlobalStyles,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getToken } from "../../../utils/utils";
import { getalllocations, getalloutlettype } from "../../assign-task/api";
import { getReportsFilterOptions_v2 } from "../api";
import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

const CLEAR_X_DATA_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
  );

const SELECT_ALL_LABEL = "Select All";

/** Rewards-style arrow */
const CustomArrow = ({
  open,
  onClick,
}: {
  open?: boolean;
  onClick?: () => void;
}) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRight: "2px solid #222",
      borderBottom: "2px solid #222",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transformOrigin: "center center",
      transition: "transform 0.18s ease",
      marginLeft: 12,
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  />
);

/** Payload consumed by parent on APPLY (STRICT: backend keys only) */
export interface ReportsFilterPayload {
  region?: string[] | null;                 // <-- NEW
  city?: string[] | null;
  asm_name?: string[] | null;
  area_code?: string[] | null;
  tse_name?: string[] | null;
  territory_code?: string[] | null;
  sfa_id?: string[] | null;
  outlet_salesforce_id?: string[] | null;
  pmi_entity_code?: string[] | null;
  outlet_name?: string[] | null;
  status?: string[] | null;
  contract_since_from?: string | null;
  contract_since_to?: string | null;
}

interface Props {
  isOpen?: boolean;
  shouldFetch?: boolean;
  onFetched?: () => void;
  onApplyFilters: (payload: ReportsFilterPayload) => void;
  onCancel?: () => void;

  dateRange: [Date | null, Date | null];
  setDateRange: Dispatch<SetStateAction<[Date | null, Date | null]>>;
  initialFilters?: Partial<ReportsFilterPayload> | null;

  fetchCountOnApply?: (payload: ReportsFilterPayload) => Promise<number>;
}

/** Rewards-style TextField wrapper for react-datepicker (clickable chevron) */
const ForwardedTextInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, style, sx, ...rest }, ref) => {
    const mergedStyle = { paddingRight: 40, ...(style || {}), width: "100%" };
    return (
      <TextField
        size="small"
        variant="outlined"
        inputRef={ref}
        onClick={onClick}
        value={value}
        placeholder={placeholder || "Select date range"}
        fullWidth
        {...rest}
        sx={{ position: "relative", width: "100%", ...(sx || {}) }}
        InputProps={{
          ...(rest.InputProps || {}),
          endAdornment: (
            <>
              {rest.InputProps?.endAdornment}
              <InputAdornment position="end" sx={{ position: "absolute", right: 10 }}>
                <CustomArrow
                  open={false}
                  onClick={() => {
                    try {
                      onClick && (onClick as unknown as () => void)();
                    } catch {
                      try {
                        onClick?.();
                      } catch {}
                    }
                  }}
                />
              </InputAdornment>
            </>
          ),
        }}
        inputProps={{
          ...(rest.inputProps || {}),
          style: mergedStyle,
        }}
      />
    );
  }
);
ForwardedTextInput.displayName = "ForwardedTextInput";

/** Compact Multi-select with Select All + search */
function CustomMultiSelectWithSearch({
  label,
  options,
  value,
  onChange,
  loading,
  formatOptionLabel,
  onOpen,
  onClose,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
  loading?: boolean;
  formatOptionLabel?: (s: string) => string;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const fmt = (s: string) => (formatOptionLabel ? formatOptionLabel(s) : s);

  useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        onClose?.();
        setSearchValue("");
      }
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        onClose?.();
        setSearchValue("");
      }
    };
    document.addEventListener("mousedown", click);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", click);
      document.removeEventListener("keydown", esc);
    };
  }, [open, onClose]);

  const filtered = (options || []).filter((opt) =>
    fmt(opt).toLowerCase().includes(searchValue.toLowerCase())
  );

  const allSelected = value.length > 0 && value.length === options.length;
  const displayedValue =
    value.length === 0 ? label : value.length <= 2 ? value.map(fmt).join(", ") : `${value.slice(0, 2).map(fmt).join(", ")}…`;

  const toggleOption = (opt: string) =>
    value.includes(opt) ? onChange(value.filter((v) => v !== opt)) : onChange([...value, opt]);

  const toggleSelectAll = () => (allSelected ? onChange([]) : onChange(options.slice()));

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", userSelect: "none" }}>
      <TextField
        size="small"
        variant="outlined"
        value={displayedValue}
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            if (next) onOpen?.(); else onClose?.();
            return next;
          });
        }}
        placeholder={label}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <CustomArrow
                  open={open}
                  onClick={() => {
                    setOpen((v) => {
                      const next = !v;
                      if (next) onOpen?.(); else onClose?.();
                      return next;
                    });
                  }}
                />
              </InputAdornment>
            </>
          ),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        fullWidth
      />

      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 3500,
            mt: 0.5,
            borderRadius: 1,
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 4,
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                marginBottom: 8,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "5px",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", mb: 1, cursor: "pointer" }}
              onClick={toggleSelectAll}
            >
              <Checkbox checked={allSelected} indeterminate={value.length > 0 && !allSelected} />
              <Typography variant="body2">{SELECT_ALL_LABEL}</Typography>
            </Box>

            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                No options found
              </Typography>
            )}

            {filtered.map((opt) => (
              <Box
                key={opt}
                sx={{ display: "flex", alignItems: "center", py: 0.5, cursor: "pointer" }}
                onClick={() => toggleOption(opt)}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Checkbox checked={value.includes(opt)} />
                <Typography variant="body2">{fmt(opt)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function ReportsTrFilterPopup({
  isOpen = false,
  shouldFetch = false,
  onFetched,
  onApplyFilters,
  onCancel,
  dateRange,
  setDateRange,
  initialFilters,
  fetchCountOnApply,
}: Props) {
  // prevent body scroll while popup open (Rewards behavior)
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = prev.position || "";
      document.body.style.top = prev.top || "";
      document.body.style.left = prev.left || "";
      document.body.style.right = prev.right || "";
      document.body.style.overflow = prev.overflow || "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  /** Server-provided options */
  const [regionOptions, setRegionOptions] = useState<string[]>([]);          // <-- NEW
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [asmOptions, setAsmOptions] = useState<string[]>([]);
  const [areaCodeOptions, setAreaCodeOptions] = useState<string[]>([]);
  const [tseOptions, setTseOptions] = useState<string[]>([]);
  const [territoryOptions, setTerritoryOptions] = useState<string[]>([]);
  const [sfaOptions, setSfaOptions] = useState<string[]>([]);
  const [outletSfIdOptions, setOutletSfIdOptions] = useState<string[]>([]);
  const [outletNameOptions, setOutletNameOptions] = useState<string[]>([]);
  const [pmiOptions, setPmiOptions] = useState<string[]>([]);
  const [statusOptions] = useState<string[]>(["active", "inactive"]);

  /** Loading flags */
  const [loadingRegion, setLoadingRegion] = useState(false);                 // <-- NEW
  const [loadingCity, setLoadingCity] = useState(false);
  const [loadingAsm, setLoadingAsm] = useState(false);
  const [loadingAreaCodes, setLoadingAreaCodes] = useState(false);
  const [loadingTse, setLoadingTse] = useState(false);
  const [loadingTerritory, setLoadingTerritory] = useState(false);
  const [loadingSfa, setLoadingSfa] = useState(false);
  const [loadingOutletSfId, setLoadingOutletSfId] = useState(false);
  const [loadingOutletNames, setLoadingOutletNames] = useState(false);
  const [loadingPmi, setLoadingPmi] = useState(false);

  /** Selections */
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);      // <-- NEW
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedAsm, setSelectedAsm] = useState<string[]>([]);
  const [selectedAreaCodes, setSelectedAreaCodes] = useState<string[]>([]);
  const [selectedTse, setSelectedTse] = useState<string[]>([]);
  const [selectedTerritories, setSelectedTerritories] = useState<string[]>([]);
  const [selectedSfaIds, setSelectedSfaIds] = useState<string[]>([]);
  const [selectedOutletSfIds, setSelectedOutletSfIds] = useState<string[]>([]);
  const [selectedPmiEntities, setSelectedPmiEntities] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);

  /** Two separate date ranges */
  const [contractSinceRange, setContractSinceRange] = useState<[Date | null, Date | null]>([null, null]);
  const [localUpdatedRange, setLocalUpdatedRange] = useState<[Date | null, Date | null]>(dateRange);

  /** Triples (optional) */
  const [masterPairs, setMasterPairs] = useState<
    { outlet_name: string; pmi_entity_code: string; city: string }[]
  >([]);

  const token = getToken();
  const filterAbortRef = useRef<AbortController | null>(null);
  const onFetchedCalledRef = useRef(false);
  const skipCityEffectRef = useRef(false);
  const foInFlightRef = useRef(false);
  const foLastKeyRef = useRef<string | null>(null);

  // Which facet is being edited + is any dropdown open?
  const lastChangedKeyRef = useRef<keyof ReportsFilterPayload | null>(null);
  const anyDropdownOpenRef = useRef(false);

  /** helpers */
  const formatDate = (d: Date | null): string | null => {
    if (!d) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const formatRangeText = (range: [Date | null, Date | null]) => {
    if (!range || (!range[0] && !range[1])) return "Select date range";
    const s = range[0] ? range[0].toLocaleDateString() : "";
    const e = range[1] ? range[1].toLocaleDateString() : "";
    return s && e ? `${s} - ${e}` : s || e || "Select date range";
  };
  const uniqSorted = (arr: ReadonlyArray<string>) =>
    Array.from(new Set(arr.filter(Boolean))).sort((a, b) => a.localeCompare(b));

  const ingestPairs = (pairs: any[]): void => {
    const clean = Array.isArray(pairs)
      ? pairs
          .map((p: any) => ({
            outlet_name: String(p?.outlet_name ?? "").trim(),
            pmi_entity_code: String(p?.pmi_entity_code ?? "").trim(),
            city: String(p?.city ?? "").trim(),
          }))
          .filter((p) => p.outlet_name && p.pmi_entity_code)
      : [];
    setMasterPairs(clean);
  };

  const buildQueryForOptions = () => ({
    region: selectedRegions.length ? selectedRegions : null,               // <-- NEW
    city: selectedCities.length ? selectedCities : null,
    asm_name: selectedAsm.length ? selectedAsm : null,
    area_code: selectedAreaCodes.length ? selectedAreaCodes : null,
    tse_name: selectedTse.length ? selectedTse : null,
    territory_code: selectedTerritories.length ? selectedTerritories : null,
    sfa_id: selectedSfaIds.length ? selectedSfaIds : null,
    outlet_salesforce_id: selectedOutletSfIds.length ? selectedOutletSfIds : null,
    pmi_entity_code: selectedPmiEntities.length ? selectedPmiEntities : null,
    outlet_name: selectedOutletNames.length ? selectedOutletNames : null,
    status: selectedStatus.length ? selectedStatus : null,
    contract_since_from: formatDate(contractSinceRange[0]),
    contract_since_to: formatDate(contractSinceRange[1]),
  });

  const setOptionsFromBE = (fo: any) => {
    const namesFromBE = Array.isArray(fo?.available_outlet_names) ? fo.available_outlet_names : [];
    const pmisFromBE = Array.isArray(fo?.available_pmi_codes) ? fo.available_pmi_codes : [];
    const regionsFromBE = Array.isArray(fo?.available_regions) ? fo.available_regions : []; // <-- NEW

    setRegionOptions(regionsFromBE);                                         // <-- NEW
    setOutletNameOptions(namesFromBE);
    setPmiOptions(pmisFromBE);

    setAsmOptions(Array.isArray(fo?.available_asm_names) ? fo.available_asm_names : []);
    setAreaCodeOptions(Array.isArray(fo?.available_area_codes) ? fo.available_area_codes : []);
    setTseOptions(Array.isArray(fo?.available_tse_names) ? fo.available_tse_names : []);
    setTerritoryOptions(Array.isArray(fo?.available_territory_codes) ? fo.available_territory_codes : []);
    setSfaOptions(Array.isArray(fo?.available_sfa_ids) ? fo.available_sfa_ids : []);
    setOutletSfIdOptions(
      Array.isArray(fo?.available_outlet_salesforce_ids) ? fo.available_outlet_salesforce_ids : []
    );

    if (Array.isArray(fo?.outletPairs)) {
      const rawCities: string[] = (fo.outletPairs as any[])
        .map((p) => String(p?.city ?? "").trim())
        .filter((s): s is string => s.length > 0);
      const citiesFromPairs: string[] = Array.from(new Set<string>(rawCities)).sort((a, b) =>
        a.localeCompare(b)
      );
      setCityOptions((prev) => uniqSorted([...(prev ?? []), ...citiesFromPairs]));
    }

    const editing = lastChangedKeyRef.current;

    if (editing !== "region")                                                // <-- NEW
      setSelectedRegions((prev) => prev.filter((v) => regionsFromBE.includes(v)));
    if (editing !== "asm_name")
      setSelectedAsm((prev) => prev.filter((v) => (fo?.available_asm_names ?? []).includes(v)));
    if (editing !== "area_code")
      setSelectedAreaCodes((prev) => prev.filter((v) => (fo?.available_area_codes ?? []).includes(v)));
    if (editing !== "tse_name")
      setSelectedTse((prev) => prev.filter((v) => (fo?.available_tse_names ?? []).includes(v)));
    if (editing !== "territory_code")
      setSelectedTerritories((prev) => prev.filter((v) => (fo?.available_territory_codes ?? []).includes(v)));
    if (editing !== "sfa_id")
      setSelectedSfaIds((prev) => prev.filter((v) => (fo?.available_sfa_ids ?? []).includes(v)));
    if (editing !== "outlet_salesforce_id")
      setSelectedOutletSfIds((prev) => prev.filter((v) => (fo?.available_outlet_salesforce_ids ?? []).includes(v)));
    if (editing !== "outlet_name")
      setSelectedOutletNames((prev) => prev.filter((v) => namesFromBE.includes(v)));
    if (editing !== "pmi_entity_code")
      setSelectedPmiEntities((prev) => prev.filter((v) => pmisFromBE.includes(v)));
  };

  const fetchFilterOptions = async (restrictCity: string[] | null = null, force = false) => {
    const baseQuery = buildQueryForOptions();
    const query: any = {
      ...baseQuery,
      ...(restrictCity && restrictCity.length ? { city: restrictCity } : {}),
    };

    const omitKey = lastChangedKeyRef.current;
    if (omitKey) delete query[omitKey];

    const key = JSON.stringify(query);
    if (!force && foLastKeyRef.current === key) return;
    if (foInFlightRef.current) return;
    foInFlightRef.current = true;

    if (filterAbortRef.current) {
      try { filterAbortRef.current.abort(); } catch {}
    }
    const ctrl = new AbortController();
    filterAbortRef.current = ctrl;

    try {
      setLoadingRegion(true);                                                // <-- NEW
      setLoadingAsm(true);
      setLoadingAreaCodes(true);
      setLoadingTse(true);
      setLoadingTerritory(true);
      setLoadingSfa(true);
      setLoadingOutletSfId(true);
      setLoadingOutletNames(true);
      setLoadingCity(true);
      setLoadingPmi(true);

      const fo = await getReportsFilterOptions_v2(query, token, ctrl.signal);
      if (ctrl.signal.aborted) return;

      ingestPairs(Array.isArray(fo?.outletPairs) ? fo.outletPairs : []);
      setOptionsFromBE(fo);
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        console.error("Error fetching TR filter options:", err);
      }
    } finally {
      if (filterAbortRef.current === ctrl) filterAbortRef.current = null;
      foLastKeyRef.current = key;
      foInFlightRef.current = false;

      setLoadingRegion(false);                                               // <-- NEW
      setLoadingAsm(false);
      setLoadingAreaCodes(false);
      setLoadingTse(false);
      setLoadingTerritory(false);
      setLoadingSfa(false);
      setLoadingOutletSfId(false);
      setLoadingOutletNames(false);
      setLoadingCity(false);
      setLoadingPmi(false);
    }
  };

  /** Initial open */
  useEffect(() => {
    if (!isOpen || !shouldFetch) return;
    let cancelled = false;

    const run = async () => {
      try {
        setLoadingCity(true);
        setLoadingAsm(true);

        const [locRes, _otRes] = await Promise.allSettled([
          getalllocations?.(),
          getalloutlettype?.(),
        ]);

        if (cancelled) return;

        if (locRes.status === "fulfilled") {
          const locVal: any = locRes.value;
          const raw =
            (Array.isArray(locVal?.data?.data) && locVal.data.data) ||
            (Array.isArray(locVal?.data) && locVal.data) ||
            (Array.isArray(locVal?.Data) && locVal.Data) ||
            (Array.isArray(locVal) && locVal) ||
            [];

          const projected: string[] = (raw as any[]).map((x: any) => {
            return typeof x === "string"
              ? x
              : String(x?.label ?? x?.name ?? x?.city ?? x?.value ?? "");
          });

          const cleaned: string[] = projected.map((s) => s.trim()).filter((s) => Boolean(s));
          const cities: string[] = Array.from(new Set<string>(cleaned)).sort((a, b) => a.localeCompare(b));

          if (cities.length) {
            setCityOptions((prev) => uniqSorted([...(prev || []), ...cities]));
          }
        } else {
          console.error("getalllocations failed:", locRes.reason);
        }

        await fetchFilterOptions(null, true);

        if (!cancelled && !onFetchedCalledRef.current) {
          onFetchedCalledRef.current = true;
          onFetched?.();
        }
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Error loading initial TR filter lists:", err);
        }
      } finally {
        if (!cancelled) {
          setLoadingCity(false);
          setLoadingAsm(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      if (filterAbortRef.current) {
        try { filterAbortRef.current.abort(); } catch {}
        filterAbortRef.current = null;
      }
      onFetchedCalledRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /** City change -> refresh dependent lists */
  useEffect(() => {
    if (!isOpen) return;
    if (skipCityEffectRef.current) {
      setLoadingOutletNames(false);
      setLoadingPmi(false);
      setLoadingOutletSfId(false);
      return;
    }
    setSelectedOutletNames([]);
    setSelectedPmiEntities([]);
    fetchFilterOptions(selectedCities.length ? selectedCities : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCities, isOpen]);

  /** Cleanup */
  useEffect(() => {
    return () => {
      if (filterAbortRef.current) {
        try { filterAbortRef.current.abort(); } catch {}
        filterAbortRef.current = null;
      }
    };
  }, []);

  /** Rehydrate from initialFilters */
  useEffect(() => {
    if (!initialFilters) return;

    setSelectedRegions(
  (initialFilters as any).region ??
  (initialFilters as any).regions ?? 
  []
);                         // <-- NEW
    setSelectedAsm(initialFilters.asm_name ?? []);
    setSelectedAreaCodes(initialFilters.area_code ?? []);
    setSelectedTse(initialFilters.tse_name ?? []);
    setSelectedTerritories(initialFilters.territory_code ?? []);
    setSelectedSfaIds(initialFilters.sfa_id ?? []);
    setSelectedOutletSfIds(initialFilters.outlet_salesforce_id ?? []);
    setSelectedPmiEntities(initialFilters.pmi_entity_code ?? []);
    setSelectedOutletNames(initialFilters.outlet_name ?? []);
    setSelectedCities(initialFilters.city ?? []);
    setSelectedStatus(initialFilters.status ?? []);

    const contractFrom = initialFilters.contract_since_from ? new Date(initialFilters.contract_since_from) : null;
    const contractTo = initialFilters.contract_since_to ? new Date(initialFilters.contract_since_to) : null;
    setContractSinceRange([contractFrom, contractTo]);

    const updStart = dateRange?.[0] ?? null;
    const updEnd = dateRange?.[1] ?? null;
    setLocalUpdatedRange([updStart, updEnd]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  /** Keep local updated range in sync with parent dateRange */
  useEffect(() => {
    setLocalUpdatedRange(dateRange);
  }, [dateRange]);

  const btnResetStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Refresh dynamic lists when filters change — but skip while any dropdown is open
  useEffect(() => {
    if (!isOpen) return;
    if (anyDropdownOpenRef.current) return;
    fetchFilterOptions(null, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    selectedRegions,                                                        // <-- NEW
    selectedCities,
    selectedAsm,
    selectedAreaCodes,
    selectedTse,
    selectedTerritories,
    selectedSfaIds,
    selectedOutletSfIds,
    selectedPmiEntities,
    selectedOutletNames,
    selectedStatus,
    contractSinceRange,
  ]);

  /** Clear */
  const clearAll = () => {
    skipCityEffectRef.current = true;

    setSelectedRegions([]);                                                  // <-- NEW
    setSelectedAsm([]);
    setSelectedAreaCodes([]);
    setSelectedTse([]);
    setSelectedTerritories([]);
    setSelectedSfaIds([]);
    setSelectedOutletSfIds([]);
    setSelectedPmiEntities([]);
    setSelectedOutletNames([]);
    setSelectedStatus([]);
    setSelectedCities([]);
    setContractSinceRange([null, null]);
    setLocalUpdatedRange([null, null]);
    setDateRange([null, null]);

    const payload: ReportsFilterPayload = {
      region: null,                                                          // <-- NEW
      city: null,
      asm_name: null,
      area_code: null,
      tse_name: null,
      territory_code: null,
      sfa_id: null,
      outlet_salesforce_id: null,
      pmi_entity_code: null,
      outlet_name: null,
      status: null,
      contract_since_from: null,
      contract_since_to: null,
    };

    try { onApplyFilters(payload); } catch {}

    setTimeout(() => {
      skipCityEffectRef.current = false;
      onCancel?.();
    }, 0);
  };

  /** Build payload — STRICT keys */
  const buildPayload = (): ReportsFilterPayload => ({
    region: selectedRegions.length ? selectedRegions.slice() : null,        // <-- NEW
    city: selectedCities.length ? selectedCities.slice() : null,
    asm_name: selectedAsm.length ? selectedAsm.slice() : null,
    area_code: selectedAreaCodes.length ? selectedAreaCodes.slice() : null,
    tse_name: selectedTse.length ? selectedTse.slice() : null,
    territory_code: selectedTerritories.length ? selectedTerritories.slice() : null,
    sfa_id: selectedSfaIds.length ? selectedSfaIds.slice() : null,
    outlet_salesforce_id: selectedOutletSfIds.length ? selectedOutletSfIds.slice() : null,
    pmi_entity_code: selectedPmiEntities.length ? selectedPmiEntities.slice() : null,
    outlet_name: selectedOutletNames.length ? selectedOutletNames.slice() : null,
    status: selectedStatus.length ? selectedStatus.slice() : null,
    contract_since_from: formatDate(contractSinceRange[0]),
    contract_since_to: formatDate(contractSinceRange[1]),
  });

  /** Apply */
  const handleApply = async () => {
    const payload = buildPayload();
    const [uStart, uEnd] = localUpdatedRange;
    setDateRange([uStart ?? null, uEnd ?? null]);

    try {
      onApplyFilters(payload);
    } catch {}

    if (fetchCountOnApply) {
      try {
        await fetchCountOnApply(payload);
      } catch {}
    }
  };

  return (
    <Box
      sx={{
        width: 420,
        maxHeight: "80vh",
        background: "#fff",
        borderRadius: 2,
        p: 0,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 2.5, borderBottom: "1px solid #eee" }}>
        <Typography sx={{ fontWeight: 600 }}>Apply Filter</Typography>
      </Box>

      <GlobalStyles
        styles={{
          '.MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: 'transparent !important' },
          '.MuiAutocomplete-option.Mui-focused': { backgroundColor: 'transparent !important' },
          '.MuiAutocomplete-option[aria-selected="true"].Mui-focused': { backgroundColor: 'transparent !important' },
          '.react-datepicker-wrapper': { width: '100% !important' },
        }}
      />

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Outlet City</Typography>
          <CustomMultiSelectWithSearch
            label="Outlet City"
            options={cityOptions}
            value={selectedCities}
            onChange={(vals) => { setSelectedCities(vals); }}
            loading={loadingCity}
          />
        </Box>
        {/* Region */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Region</Typography>
          <CustomMultiSelectWithSearch
            label="Region"
            options={regionOptions}
            value={selectedRegions}
            onChange={(vals) => { setSelectedRegions(vals); }}
            loading={loadingRegion}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Contracted Since</Typography>
          <Box sx={{ width: "100%" }}>
            <GlobalStyles styles={{ ".react-datepicker-wrapper": { width: "100% !important" } }} />
            <DatePicker
              selectsRange
              startDate={localUpdatedRange[0] ?? undefined}
              endDate={localUpdatedRange[1] ?? undefined}
              onChange={(range: [Date | null, Date | null]) => setLocalUpdatedRange(range)}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={({ date, decreaseMonth, increaseMonth, decreaseYear, increaseYear }) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button type="button" onClick={decreaseYear} aria-label="Previous year" style={btnResetStyle}>
                    <img src={doubleArrowLeft} alt="Previous year" />
                  </button>
                  <button type="button" onClick={decreaseMonth} aria-label="Previous month" style={btnResetStyle}>
                    <img src={arrowLeft} alt="Previous month" />
                  </button>
                  <span style={{ margin: "0 10px", fontWeight: 600 }}>
                    {date.toLocaleString("default", { year: "numeric", month: "long" })}
                  </span>
                  <button type="button" onClick={increaseMonth} aria-label="Next month" style={btnResetStyle}>
                    <img src={arrowRight} alt="Next month" />
                  </button>
                  <button type="button" onClick={increaseYear} aria-label="Next year" style={btnResetStyle}>
                    <img src={doubleArrowRight} alt="Next year" />
                  </button>
                </div>
              )}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(localUpdatedRange)}
                  key={`updated-${localUpdatedRange[0]?.getTime() ?? 0}-${localUpdatedRange[1]?.getTime() ?? 0}`}
                  InputProps={{
                    endAdornment: (localUpdatedRange[0] || localUpdatedRange[1]) ? (
                      <img
                        onClick={(e) => { e.stopPropagation(); setLocalUpdatedRange([null, null]); }}
                        src={CLEAR_X_DATA_URI}
                        alt="Clear"
                        style={{ height: 18, width: 18, cursor: "pointer", marginRight: 10, paddingTop: "4px" }}
                      />
                    ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>
        {/* City */}

        {/* ASM Name */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>ASM Name</Typography>
          <CustomMultiSelectWithSearch
            label="ASM Name"
            options={asmOptions}
            value={selectedAsm}
            onChange={(vals) => { setSelectedAsm(vals); }}
            loading={loadingAsm}
          />
        </Box>

        {/* Area Code */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Area Code</Typography>
          <CustomMultiSelectWithSearch
            label="Area Code"
            options={areaCodeOptions}
            value={selectedAreaCodes}
            onChange={(vals) => { setSelectedAreaCodes(vals); }}
            loading={loadingAreaCodes}
          />
        </Box>

        {/* TSE Name */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>TSE Name</Typography>
          <CustomMultiSelectWithSearch
            label="TSE Name"
            options={tseOptions}
            value={selectedTse}
            onChange={(vals) => { setSelectedTse(vals); }}
            loading={loadingTse}
          />
        </Box>

        {/* Territory Code */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Territory Code</Typography>
          <CustomMultiSelectWithSearch
            label="Territory Code"
            options={territoryOptions}
            value={selectedTerritories}
            onChange={(vals) => { setSelectedTerritories(vals); }}
            loading={loadingTerritory}
          />
        </Box>

        {/* SFA ID */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>SFA ID</Typography>
          <CustomMultiSelectWithSearch
            label="SFA ID"
            options={sfaOptions}
            value={selectedSfaIds}
            onChange={(vals) => { setSelectedSfaIds(vals); }}
            loading={loadingSfa}
          />
        </Box>

        {/* Outlet Salesforce ID */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Outlet Salesforce ID</Typography>
          <CustomMultiSelectWithSearch
            label="Outlet Salesforce ID"
            options={outletSfIdOptions}
            value={selectedOutletSfIds}
            onChange={(vals) => { setSelectedOutletSfIds(vals); }}
            loading={loadingOutletSfId}
          />
        </Box>

        {/* PMI Entity Code */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>PMI Entity Code</Typography>
          <CustomMultiSelectWithSearch
            label="PMI Entity Code"
            options={pmiOptions}
            value={selectedPmiEntities}
            onChange={(vals) => { setSelectedPmiEntities(vals); }}
            loading={loadingPmi}
          />
        </Box>

        {/* Outlet Name */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Outlet Name</Typography>
          <CustomMultiSelectWithSearch
            label="Outlet Name"
            options={outletNameOptions}
            value={selectedOutletNames}
            onChange={(vals) => { setSelectedOutletNames(vals); }}
            loading={loadingOutletNames}
          />
        </Box>

        {/* Status
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Status</Typography>
          <CustomMultiSelectWithSearch
            label="Status"
            options={statusOptions}
            value={selectedStatus}
            onChange={(vals) => { setSelectedStatus(vals); }}
            loading={false}
          />
        </Box> */}
        
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2.5,
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
        }}
      >
        <Button
          onClick={clearAll}
          sx={{ border: "1px solid #E7E8E9", color: "#000", borderRadius: 2, height: 40 }}
        >
          CLEAR ALL
        </Button>
        <Button
          onClick={handleApply}
          sx={{ border: "1px solid #C8172F", color: "#C8172F", borderRadius: 2, height: 40 }}
        >
          APPLY
        </Button>
      </Box>
    </Box>
  );
}
